import { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  articlesAPI,
  PortalData,
  ArticleSummary,
} from '../../services/api';
import {
  FileText,
  Search,
  ChevronRight,
  Loader2,
  BarChart3,
  DollarSign,
  LineChart,
  Users,
  Globe,
  Newspaper,
} from 'lucide-react';

// Category icon and color configuration
const CATEGORY_CONFIG: Record<string, { icon: ReactElement; color: string; gradient: string }> = {
  overview: {
    icon: <FileText className="w-4 h-4" />,
    color: 'text-emerald-400',
    gradient: 'from-emerald-500 to-cyan-500',
  },
  classification: {
    icon: <BarChart3 className="w-4 h-4" />,
    color: 'text-blue-400',
    gradient: 'from-blue-500 to-indigo-500',
  },
  historical: {
    icon: <LineChart className="w-4 h-4" />,
    color: 'text-indigo-400',
    gradient: 'from-indigo-500 to-violet-500',
  },
  fundamentals: {
    icon: <BarChart3 className="w-4 h-4" />,
    color: 'text-purple-400',
    gradient: 'from-purple-500 to-fuchsia-500',
  },
  valuation: {
    icon: <DollarSign className="w-4 h-4" />,
    color: 'text-amber-400',
    gradient: 'from-amber-500 to-orange-500',
  },
  market: {
    icon: <LineChart className="w-4 h-4" />,
    color: 'text-rose-400',
    gradient: 'from-rose-500 to-pink-500',
  },
  sentiment: {
    icon: <Users className="w-4 h-4" />,
    color: 'text-red-400',
    gradient: 'from-red-500 to-orange-500',
  },
  context: {
    icon: <Globe className="w-4 h-4" />,
    color: 'text-cyan-400',
    gradient: 'from-cyan-500 to-teal-500',
  },
};

// Article Card Component
interface ArticleCardProps {
  article: ArticleSummary;
}

function ArticleCard({ article }: ArticleCardProps): ReactElement {
  const config = CATEGORY_CONFIG[article.category] || CATEGORY_CONFIG.overview;

  return (
    <Link
      to={`/articles/${article.id}`}
      className="group block bg-slate-900/50 border border-slate-700/50 rounded-xl overflow-hidden hover:bg-slate-900/80 hover:border-slate-600 transition-all duration-200"
    >
      {/* Category accent line */}
      <div className={`h-0.5 bg-gradient-to-r ${config.gradient}`} />

      <div className="p-4">
        {/* Title */}
        <h3 className="text-sm font-medium text-slate-200 group-hover:text-white transition-colors line-clamp-2 mb-2" style={{ fontFamily: 'var(--font-display)' }}>
          {article.title}
        </h3>

        {/* Subtitle (if present) */}
        {article.subtitle && (
          <p className="text-xs text-slate-500 line-clamp-1 mb-3">
            {article.subtitle}
          </p>
        )}

        {/* Footer: Tickers + Time */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 flex-wrap">
            {article.tickers.slice(0, 3).map((ticker) => (
              <span
                key={ticker}
                className="px-1.5 py-0.5 text-[10px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded"
              >
                {ticker}
              </span>
            ))}
            {article.tickers.length > 3 && (
              <span className="text-[10px] text-slate-500">
                +{article.tickers.length - 3}
              </span>
            )}
          </div>
          <span className="text-[10px] text-slate-500">{article.time_ago}</span>
        </div>
      </div>
    </Link>
  );
}

// Category Section Component
interface CategorySectionProps {
  categoryKey: string;
  name: string;
  description: string;
  articles: ArticleSummary[];
  totalCount: number;
}

function CategorySection({ categoryKey, name, articles, totalCount }: CategorySectionProps): ReactElement {
  const config = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG.overview;

  if (articles.length === 0) {
    return <></>;
  }

  return (
    <section className="mb-10">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
            <div className="text-white">{config.icon}</div>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-100" style={{ fontFamily: 'var(--font-display)' }}>
              {name}
            </h2>
          </div>
          {totalCount > articles.length && (
            <span className="px-2 py-0.5 text-[10px] font-mono text-slate-400 bg-slate-800 border border-slate-700 rounded-full">
              {totalCount} total
            </span>
          )}
        </div>
        {totalCount > articles.length && (
          <Link
            to={`/articles/category/${categoryKey}`}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-300 transition-colors"
          >
            View All
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>

      {/* Articles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {articles.map((article) => (
          <ArticleCard key={article.id} article={article} />
        ))}
      </div>
    </section>
  );
}

// Main Portal Component
export default function ArticlesPortal(): ReactElement {
  // Fetch portal data
  const { data: portalData, isLoading, error } = useQuery<PortalData>({
    queryKey: ['articles-portal'],
    queryFn: async () => {
      const response = await articlesAPI.getPortalData(4);
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Category order for display
  const categoryOrder = ['overview', 'classification', 'historical', 'fundamentals', 'valuation', 'market', 'sentiment', 'context'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Background pattern */}
      <div className="fixed inset-0 opacity-40 pointer-events-none">
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(16, 185, 129, 0.08) 0%, transparent 50%)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-4 mb-3">
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-emerald-500/25 terminal-glow">
                  <Newspaper className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-slate-950 terminal-pulse" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white tracking-tight" style={{ fontFamily: 'var(--font-display)' }}>
                  Research
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400/80">AI-Generated</span>
                  <span className="w-1 h-1 rounded-full bg-slate-600" />
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Financial Analysis</span>
                </div>
              </div>
            </div>
          </div>
          {portalData && (
            <div className="text-right">
              <span className="text-2xl font-bold text-slate-100 font-mono">{portalData.total_articles}</span>
              <p className="text-xs text-slate-500">articles</p>
            </div>
          )}
        </div>

        {/* Search Bar */}
        <div className="mb-10">
          <div className="relative max-w-2xl group">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-xl blur-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-500" />
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
              <input
                type="text"
                placeholder="Search by ticker (e.g., AAPL, MSFT)..."
                className="w-full pl-12 pr-4 py-4 bg-slate-900/80 border border-slate-700/80 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/10 transition-all font-mono text-sm backdrop-blur-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const value = (e.target as HTMLInputElement).value.toUpperCase();
                    if (value) {
                      window.location.href = `/articles/ticker/${value}`;
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-emerald-500 mb-4" />
            <p className="text-slate-400">Loading articles...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
              <FileText className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-200 mb-2">Failed to load articles</h3>
            <p className="text-sm text-slate-500">Please try again later</p>
          </div>
        )}

        {/* Category Sections */}
        {portalData && !isLoading && (
          <>
            {categoryOrder.map((categoryKey) => {
              const categoryData = portalData.categories[categoryKey];
              if (!categoryData) return null;

              return (
                <CategorySection
                  key={categoryKey}
                  categoryKey={categoryKey}
                  name={categoryData.name}
                  description={categoryData.description}
                  articles={categoryData.articles}
                  totalCount={categoryData.total_count}
                />
              );
            })}

            {/* Empty State */}
            {portalData.total_articles === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-800 flex items-center justify-center">
                  <FileText className="w-8 h-8 text-slate-600" />
                </div>
                <h3 className="text-lg font-medium text-slate-200 mb-2">No articles yet</h3>
                <p className="text-sm text-slate-500">Check back later for AI-generated financial analysis</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
